/* eslint-disable max-lines */
/*
 * The Jupiter top navigation bar.
 *
 * JupiterTopNav wraps a Cloudscape TopNavigation and adds the hamburger that
 * toggles the side navigation rail. Identity, utilities, search and i18n strings
 * are pass-throughs, so every Policy menu option below is unchanged from the
 * Cloudscape bar this replaced — only the shell around them is new.
 */
// Must precede the global-nav import: the package calls React 18's useId, which does
// not exist on this app's React 16. See the shim for details.
import 'utils/reactUseIdShim';
import React, { Suspense, lazy } from 'react';
import { action, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import { NAVIGATION } from 'constants/navigation';
import './appTopNavStyles.scss';
import AppStore from '../appStore';
import { Hotspot, Input, Icon } from '@amzn/awsui-components-react/polaris';
import UserAvatar from 'components/userAvatar/userAvatar';
import { JupiterTopNav } from '@amzn/core-grc-global-nav-frontend';
import '@amzn/core-grc-global-nav-frontend/styles';
import Modal from 'components/modal/modal';
import { logoSrc } from 'components/logo/logo';
import NightlightIcon from '@mui/icons-material/Nightlight';
import { Cookie } from 'cookiesStore/cookieStore';
import Brightness5Icon from '@mui/icons-material/Brightness5';
import { getFolderMetadata, getPreferences } from 'api/graphql/query';

const AddReactionOutlinedIcon = lazy(() => import('@mui/icons-material/AddReactionOutlined'));
const ManageSearchIcon = lazy(() => import('@mui/icons-material/ManageSearch'));
const Form = lazy(() => import('form/form'));
const WhatsNewPanel = lazy(() => import('components/whatsNew/WhatsNewPanel'));

/** Suffix Cloudscape appends to the accessible name of links that open a new tab. */
const EXTERNAL_ICON_ARIA_LABEL = ' (opens in new tab)';

/** Id of the profile-menu item that jumps to the user's configured organization. */
const MY_ORG_ITEM_ID = 'myOrg';

/**
 * Id on the inner Cloudscape TopNavigation. Kept from the previous bar because
 * `appStore.addAccessibilitySettings` and the stylesheet both key off it.
 */
export const TOP_NAV_ID = 'policy--topnav';

/**
 * Id on the outer wrapper. AppLayout's `headerSelector` measures this, not
 * `TOP_NAV_ID`, because the hamburger sits outside the Cloudscape bar and would
 * otherwise be left out of the measured header height.
 */
export const TOP_NAV_CONTAINER_ID = 'policy--topnav-container';

interface Props {
  appStore?: AppStore;
  isPlaceholder?: boolean;
}

@inject('appStore') @observer
export default class AppTopNav extends React.PureComponent<Props, unknown> {
  @observable loading = true;
  @observable searchQuery = '';
  @observable darkModeIcon = document.body.classList.contains('awsui-polaris-dark-mode') ? (
    <Suspense fallback={<></>}>
      <Brightness5Icon />
    </Suspense>
  ) : (
    <Suspense fallback={<></>}>
      <NightlightIcon />
    </Suspense>
  );

  renderCreateFolderModal(): any {
    const { appStore } = this.props;
    return (
      <Suspense fallback={<></>}>
        <Modal
          primaryButtonLabel="Create Folder"
          modalStore={appStore!.appTopNavStore.folderModalStore}
          size={'medium'}
          primaryButtonDisabled={!appStore!.appTopNavStore.folderModalStore.isFormValid}
          onPrimary={appStore!.appTopNavStore.handleCreateFolder}
          onCancel={appStore!.appTopNavStore.handleCreateFolderCancelClick}
        >
          <Suspense fallback={<></>}>
            <Form formStore={appStore!.appTopNavStore.folderModalStore} groupContainer={false} />
          </Suspense>
        </Modal>
      </Suspense>
    );
  }

  renderCreateDocumentModal(): any {
    const { appStore } = this.props;
    const { documentModalStore: { documentType, templateIdentifier } } = appStore!.appTopNavStore;
    const docType = appStore?.getDocumentTypeModel(documentType)?.label;
    return (
      <Suspense fallback={<></>}>
        <Modal
          primaryButtonLabel={templateIdentifier ? `Create ${docType} Template` : `Create ${docType}`}
          modalStore={appStore!.appTopNavStore.documentModalStore}
          size={'medium'}
          primaryButtonDisabled={!appStore!.appTopNavStore.documentModalStore.isFormValid}
          onPrimary={appStore!.appTopNavStore.handleCreateDocument}
          onCancel={appStore!.appTopNavStore.handleCreateDocumentCancelClick}
        >
          <Suspense fallback={<></>}>
            <Form formStore={appStore!.appTopNavStore.documentModalStore} groupContainer={false} />
          </Suspense>
        </Modal>
      </Suspense>
    );
  }


  renderPrferenceModal(): any {
    const { appStore } = this.props;

    return (
      <Suspense fallback={<></>}>
        <Modal
          modalStore={appStore!.appTopNavStore.preferenceModalStore}
          onPrimary={appStore!.appTopNavStore.handleSavePreference}
          primaryButtonLabel={'Submit'}
        >
          <Suspense fallback={<></>}>
            <Form formStore={appStore!.appTopNavStore.preferenceModalStore} groupContainer={false} />
          </Suspense>
        </Modal>
      </Suspense>
    );
  }

  @action.bound
  setSearchQuery(searchQuery: string) {
    this.searchQuery = searchQuery;
  }

  @action.bound
  handleKeyDown(e) {
    if (e.detail.key === 'Enter') {
      this.navigateToSearchPage(this.searchQuery);
    }
  }

  navigateToSearchPage(searchQuery = ''): void {
    const { appStore } = this.props;
    appStore!.appTopNavStore.onSearch(searchQuery);
  }

  @action.bound
  handleInput(e) {
    this.setSearchQuery(e.detail.value);
  }

  /**
   * Toggles the side navigation rail.
   */
  @action.bound
  handleHamburgerClick(): void {
    const { appStore } = this.props;
    appStore?.handleIsSideNavOpen(!appStore?.isSideNavOpen);
  }

  /**
   * Dismisses the feedback popover when the bar is clicked, treating the click as
   * "outside" the popover. Lives here rather than on a wrapper in AppContainer, because
   * the bar's container is the sticky element and must stay a direct child of the app
   * root — see the note where AppTopNav is rendered.
   */
  @action.bound
  handleTopNavClick(): void {
    const { appStore } = this.props;
    if (appStore?.isFeedbackVisible) appStore.showFeedback(false);
  }

  renderSearchBar(): any {
    const { appStore } = this.props;
    const isSearchPage = window.location.pathname?.includes('/search/');

    return (
      <>
        {appStore?.hideSearchBar || isSearchPage ?
          <></> :
          <section className="policy--topnav-searchbar" data-testid="topnav-search-bar">
            <Input
              name="search"
              value={this.searchQuery}
              onChange={this.handleInput}
              onKeyDown={this.handleKeyDown}
              placeholder="Policies, procedures, and standards search"
              type="search"
              className="policy--topnav-searchbar-input"
            />
            <span className="policy--advanced-search-button" title="Advanced Search" onClick={() => this.navigateToSearchPage()} data-testid="topnav-advanced-search-button">
              <Suspense fallback={<></>}>
                <ManageSearchIcon />
              </Suspense>
            </span>
          </section >
        }
      </>
    );
  }

  /**
   * Resolves the user's configured organization folder, used by the "My Org" menu
   * item. Fetched once on mount: it is only read when that item is clicked, and this
   * previously ran from `render()`, firing a query on every re-render.
   */
  componentDidMount(): void {
    this.getMyOrgId();
    const { appStore, isPlaceholder } = this.props;
    if (!isPlaceholder) {
      appStore?.appTopNavStore?.maybeAutoOpenWhatsNew();
    }
  }

  getMyOrgId() {
    const { appStore } = this.props;

    appStore?.api?.query(getPreferences, {})
      .then(action(({ data }) => {
        const preferences = JSON.parse(data.getPreferences);
        appStore!.appTopNavStore.organizationUrl = preferences?.organizationUrl;
      }))
      // Runs once on mount rather than per render, so there is no implicit retry: a
      // failure here would otherwise be swallowed and "My Org" would silently fall
      // back to the site origin. Log it so the failure is visible.
      .catch((error) => {
        console.error('Failed to resolve My Org preferences: ', error);
      });
  }
  handleOrgRedirect() {
    const { appStore } = this.props;

    if (appStore?.appTopNavStore.organizationUrl) {
      appStore?.api?.query(getFolderMetadata, { folderId: appStore?.appTopNavStore.organizationUrl })
        .then(action(folderData => {
          const folderMetadata = folderData.data.getFolderMetadata;
          const prefix = folderMetadata.folderAlias.length ? 'org' : 'folder';
          const folderSuffix = folderMetadata.folderAlias.length ? folderMetadata.folderAlias : folderMetadata.id;
          const url = `${window.location.origin}/${prefix}/${folderSuffix}`;
          window.location.href = url;
        }))
        // Without this a rejected lookup is an unhandled rejection and the redirect
        // silently never fires. Fall back to the site origin — the same destination as
        // the no-organizationUrl branch below — and match the .catch on getMyOrgId.
        .catch((error) => {
          console.error('Failed to resolve My Org folder metadata: ', error);
          window.location.href = window.location.origin;
        });
    } else {
      const url = `${window.location.origin}`;
      window.location.href = url;
    }
  }
  /**
   * Routes clicks in the profile menu. Items carrying an `href` navigate on their own;
   * the rest are dispatched here.
   * @param {any} e Cloudscape menu-dropdown click event.
   */
  @action.bound
  handleProfileMenuClick(e: any): void {
    const { appStore } = this.props;
    const id = e?.detail?.id;

    if (id === MY_ORG_ITEM_ID) {
      this.handleOrgRedirect();
      return;
    }
    appStore?.appTopNavStore.handleMenuClick(id);
  }

  render(): any {
    const { appStore, isPlaceholder } = this.props;

    return (
      <>
        {/* `.top-navigation` carries the package's `position: sticky; top: 0` — this
            element must remain a direct child of the app root for that to have any
            travel. See the note where AppContainer renders AppTopNav. */}
        <div
          id={TOP_NAV_CONTAINER_ID}
          className="top-navigation"
          data-testid="topnav-container"
          onClick={this.handleTopNavClick}
        >
        <JupiterTopNav
          showHamburgerMenu
          isNavOpen={appStore?.isSideNavOpen ?? true}
          onHamburgerClick={this.handleHamburgerClick}
          id={TOP_NAV_ID}
          identity={{
            href: `${NAVIGATION.LANDING_PAGE.PATH}`,
            logo: {
              // An 86x20 inline SVG data URI, so it renders at its intrinsic size and
              // needs no height override. See components/logo/logo.tsx.
              src: logoSrc, alt: 'Amazon GRC',
            },
          }}
          utilities={isPlaceholder ? [] : [
            {
              type: 'menu-dropdown',
              ariaLabel: 'Create New Item',
              text: 'New',
              onItemClick: item => appStore?.appTopNavStore!.handleCreateClick(item),
              items: [
                {
                  text: 'Folder',
                  id: 'folder',
                },
                {
                  text: 'Document',
                  items: appStore!.documentTypes?.map(type => ({
                    text: type.label, id: `document-${type.id}`,
                  })) || [],
                },
                {
                  text: 'Template',
                  items: appStore!.templateTypes?.map(type => ({
                    text: type.label, id: `template-${type.id}`,
                  })) || [],
                },
              ],
            },
            {
              type: 'button',
              variant: 'link',
              external: false,
              badge: !!appStore?.appTopNavStore?.whatsNewEnabled,
              onClick: e => {
                e.stopPropagation();
                // Fetch page 1 only on the FIRST open (whatsNewLoaded guards it); reopening
                // must not re-fetch the same feed, and closing never fetches.
                if (!appStore?.appTopNavStore?.showWhatsNew && !appStore?.appTopNavStore?.whatsNewLoaded) {
                  appStore?.appTopNavStore?.loadWhatsNew();
                }
                appStore?.appTopNavStore?.toggleWhatsNew();
              },
              iconSvg: (
                <div id="policy--whats-new-icon" data-testid="topnav-whats-new-icon" style={{ color: appStore?.appTopNavStore?.showWhatsNew ? '#539fe5' : 'inherit' }}>
                  <Icon name="notification" />
                </div>
              ),
              ariaLabel: "What's new",
            },
            {
              type: 'button',
              variant: 'link',
              external: false,
              onClick: e => {
                e.stopPropagation();
                appStore?.showFeedback(!appStore.isFeedbackVisible, true);
              },
              iconSvg: (
                <div id="policy--feedback-icon" data-testid="topnav-feedback-icon" style={{ color: appStore?.isFeedbackVisible ? '#539fe5' : 'inherit' }}>
                  <Suspense fallback={<></>}>
                    <AddReactionOutlinedIcon />
                  </Suspense>
                </div>
              ),
              ariaLabel: 'Feedback',
            },
            {
              type: 'button',
              variant: 'link',
              external: false,
              onClick: action(e => {
                e.stopPropagation();
                document.body.classList.toggle('awsui-polaris-dark-mode');

                if (document.body.classList.contains('awsui-polaris-dark-mode')) {
                  appStore?.cookiesStore?.current?.setCookie(Cookie.DARK_MODE_ACTIVE);
                  document.body.classList.add('darkMode');
                  this.darkModeIcon = (
                    <Suspense fallback={<></>}>
                      <Brightness5Icon />
                    </Suspense>
                  );
                } else {
                  appStore?.cookiesStore?.current.removeCookie(Cookie.DARK_MODE_ACTIVE);
                  document.body.classList.remove('darkMode');
                  this.darkModeIcon = (
                    <Suspense fallback={<></>}>
                      <NightlightIcon />
                    </Suspense>
                  );
                }
              }),
              iconSvg: this.darkModeIcon,
              ariaLabel: 'Toggle dark mode',
            },
            {
              type: 'menu-dropdown',
              text: appStore?.currentUserID ?? '',
              iconName: 'user-profile',
              iconSvg: (
                <span title="Phone Tool Link" data-testid="topnav-user-profile" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {this.props.appStore?.tutorialData?.tasks && (
                    <span className="hot-spot">
                      <Hotspot direction="left" hotspotId="menu" />
                    </span>
                  )}
                  <UserAvatar
                    userAlias={this.props.appStore?.currentUserID ?? ''}
                    size="small"
                    testId="topnav-profile-picture"
                  />
                </span>
              ),
              ariaLabel: 'Phone Tool Link',
              onItemClick: this.handleProfileMenuClick,
              items: [
                {
                  id: MY_ORG_ITEM_ID,
                  text: 'My Org',
                },
                {
                  id: 'quickSightDashboard',
                  text: 'QuickSight Dashboard',
                  href: NAVIGATION.QUICKSIGHT_DASHBOARD.PATH,
                  external: true,
                  externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                },
                {
                  id: 'trainingVideos',
                  text: 'Training Videos',
                  href: 'https://atoz.amazon.work/learn/trainings/LPRLERN20250408145516db0845c7?lms=LEARN',
                  external: true,
                  externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                },
                {
                  id: 'howTo',
                  text: 'How To Create and Manage...',
                  // These are Policy documents served by this app, so they are in-app
                  // links rather than external ones.
                  items: [
                    {
                      id: 'howToFolders',
                      text: 'Folders',
                      href: 'https://policy.a2z.com/docs/501045/publication',
                      external: false,
                    },
                    {
                      id: 'howToPermissions',
                      text: 'Permissions',
                      href: 'https://policy.a2z.com/docs/501061/publication',
                      external: false,
                    },
                    {
                      id: 'howToPolicies',
                      text: 'Policies',
                      href: 'https://policy.a2z.com/docs/501049/publication',
                      external: false,
                    },
                    {
                      id: 'howToApprovalWorkflows',
                      text: 'Approval Workflows',
                      href: 'https://policy.a2z.com/docs/501221/publication',
                      external: false,
                    },
                    {
                      id: 'howToExceptions',
                      text: 'Exceptions',
                      href: 'https://policy.a2z.com/docs/592198/publication',
                      external: false,
                    },
                    {
                      id: 'howToScheduledReviews',
                      text: 'Scheduled Reviews',
                      href: 'https://policy.a2z.com/docs/594305/publication',
                      external: false,
                    },
                    {
                      id: 'howToBulkActions',
                      text: 'Bulk Actions',
                      href: 'https://policy.a2z.com/docs/607819/publication',
                      external: false,
                    },
                    {
                      id: 'howToReporting',
                      text: 'Reporting',
                      href: 'https://policy.a2z.com/docs/621211/publication',
                      external: false,
                    },
                  ],
                },
                {
                  id: 'info',
                  text: 'Info',
                  external: true,
                  items: [
                    {
                      id: 'contactUs',
                      text: 'Contact Us',
                      href: 'https://w.amazon.com/bin/view/FinTech/AmazonPolicy/Contactus/officehours/',
                      external: true,
                      externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                    },
                    {
                      id: 'emailList',
                      text: 'Email List',
                      href: 'https://email-list.amazon.com/email-list/expand-list/policyinterest',
                      external: true,
                      externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                    },
                    {
                      id: 'updates',
                      text: 'Updates',
                      href: 'https://w.amazon.com/bin/view/FinTech/AmazonPolicy/',
                      external: true,
                      externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                    },
                    {
                      id: 'userGuide',
                      text: 'User Guide',
                      href: 'https://policy.a2z.com/docs/604045/publication',
                      external: true,
                      externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                    },
                  ],
                },
                {
                  id: 'submitRequest',
                  text: 'Request',
                  external: true,
                  items: [
                    {
                      id: 'bugFix',
                      text: 'Bug Fix',
                      href: 'https://issues.amazon.com/issues/create?template=7237de9e-1b31-4b64-8c96-62be3db8192a',
                      external: true,
                      externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                    },
                    {
                      id: 'change',
                      text: 'Change',
                      href: 'https://issues.amazon.com/issues/create?template=a870525e-f993-43a0-81ad-65ed2f0e75de',
                      external: true,
                      externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                    },
                    {
                      id: 'feature',
                      text: 'Feature',
                      href: 'https://issues.amazon.com/issues/create?template=75cc4d00-8e5d-4056-a6a5-eb96894df5a6',
                      external: true,
                      externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                    },
                    {
                      id: 'generalInquiry',
                      text: 'General Inquiry',
                      href: 'https://issues.amazon.com/issues/create?template=d1243a39-24e9-40ba-a8e9-92beb599d169',
                      external: true,
                      externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                    },
                    {
                      id: 'permission',
                      text: 'Permission',
                      href: 'https://issues.amazon.com/issues/create?template=33540d58-0506-402f-b388-72762e3532a5',
                      external: true,
                      externalIconAriaLabel: EXTERNAL_ICON_ARIA_LABEL,
                    },
                  ],
                },
              ],
            },
          ]}
          i18nStrings={{
            searchIconAriaLabel: 'Search',
            searchDismissIconAriaLabel: 'Close search',
            overflowMenuTriggerText: 'More',
            overflowMenuTitleText: '',
          }}
          search={isPlaceholder ? <></> : this.renderSearchBar()}
        />
        </div>

        {/* What's New announcements side panel */}
        {!isPlaceholder && (
          <Suspense fallback={<></>}>
            <WhatsNewPanel
              isOpen={!!appStore?.appTopNavStore?.showWhatsNew}
              onClose={() => appStore?.appTopNavStore?.toggleWhatsNew(false)}
              entries={appStore?.appTopNavStore?.whatsNewEntries ?? []}
              loading={!!appStore?.appTopNavStore?.whatsNewLoading}
              loadingMore={!!appStore?.appTopNavStore?.whatsNewLoadingMore}
              hasMore={!!appStore?.appTopNavStore?.whatsNewCursor}
              loadMoreFailed={!!appStore?.appTopNavStore?.whatsNewLoadMoreFailed}
              onLoadMore={() => appStore?.appTopNavStore?.loadMoreWhatsNew()}
              onRetryLoadMore={() => appStore?.appTopNavStore?.retryLoadMoreWhatsNew()}
              onNavigate={path => appStore?.navigateTo(path)}
            />
          </Suspense>
        )}

        {/* Modals for the New dropdown button.
            Not wrapped in elements: Cloudscape portals modal content to the body, so a
            wrapper contributes nothing but layout. As inline <span>s they generated a
            line box in the sticky top-nav container, which showed up as a strip of
            white page background between the bar and the content below it. */}
        {!isPlaceholder && appStore?.appTopNavStore?.folderModalStore && this.renderCreateFolderModal()}
        {!isPlaceholder && appStore?.appTopNavStore?.documentModalStore && this.renderCreateDocumentModal()}
        {appStore?.appTopNavStore?.preferenceModalStore && appStore?.appTopNavStore?.showPreference && this.renderPrferenceModal()}
      </>
    );
  }
}
